import { createBrowserRouter, Navigate } from 'react-router';
import { AppShell } from './components/AppShell.tsx';
import { Home, RequireAuth } from './components/RouteGuards.tsx';
import { AuthCallback } from './pages/AuthCallback.tsx';
import { Credits } from './pages/Credits.tsx';
import { ErrorPage } from './pages/ErrorPage.tsx';
import { Game } from './pages/Game.tsx';
import { GuestPlay } from './pages/GuestPlay.tsx';
import { Harbour } from './pages/Harbour.tsx';
import { HowToPlayPage } from './pages/HowToPlayPage.tsx';
import { Join } from './pages/Join.tsx';
import { Logbook } from './pages/Logbook.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Profile } from './pages/Profile.tsx';
import { ShipsPage } from './pages/ShipsPage.tsx';

export const router = createBrowserRouter([
  {
    path: '/',
    ErrorBoundary: ErrorPage,
    children: [
      { index: true, Component: Home },
      { path: 'auth/callback', Component: AuthCallback },
      { path: 'credits', Component: Credits },
      // The rules in short, open to guests (docs/ux.md U-LEARN-01, Q-UX-08).
      { path: 'how-to-play/:topic?', Component: HowToPlayPage },
      // The ship classes side by side, open to guests too.
      { path: 'ships', Component: ShipsPage },
      // An invite to an online game: guests see it too, then sign in to take it.
      { path: 'join/:code', Component: Join },
      // A game against the computer on the small map, kept by the browser: no account needed.
      { path: 'play', Component: GuestPlay },
      {
        Component: RequireAuth,
        children: [
          {
            Component: AppShell,
            children: [
              // The captain's home port, first after signing in; the full log a click away.
              { path: 'harbour', Component: Harbour },
              { path: 'profile', Component: Profile },
              // The cabin was the profile: old links land there.
              { path: 'cabin', element: <Navigate to="/profile" replace /> },
              { path: 'logbook', Component: Logbook },
            ],
          },
          // The game screen fills the window: no shell around it.
          { path: 'games/:id', Component: Game },
          // Dev-only sandbox: the game screen with the dev tools, on a server game. Dropped
          // from production builds (`import.meta.env.DEV` is false there).
          ...(import.meta.env.DEV
            ? [
                {
                  path: 'dev/sandbox',
                  lazy: async () => ({
                    Component: (await import('./dev/sandbox/SandboxPage.tsx')).SandboxPage,
                  }),
                },
              ]
            : []),
        ],
      },
      // Dev-only galleries, dropped from production builds.
      ...(import.meta.env.DEV
        ? [
            {
              path: 'dev/flags',
              lazy: async () => ({ Component: (await import('./dev/flags/FlagsGallery.tsx')).FlagsGallery }),
            },
            {
              path: 'dev/ships',
              lazy: async () => ({ Component: (await import('./dev/ships/ShipsGallery.tsx')).ShipsGallery }),
            },
          ]
        : []),
      { path: '*', Component: NotFound },
    ],
  },
]);
